import {
    chartCalendarGetIntensityPercentile,
    type VxChartCalendarIntensityLevel,
    type VxChartCalendarItemType,
} from '@voxie/frontend-components';
import { createGlobalState } from '@vueuse/core';
import type { Dayjs } from 'dayjs';
import { ref } from 'vue';
import type { SenderType } from '~/constants/senderTypes';
import blastForecastClient from '~/services/blastForecast/blastForecastClient';
import type { GetBlastForecastRequest } from '~/services/blastForecast/blastForecastClient.types';
import type { ClientContext } from '~/services/contextClient';
import { getClientTz } from '~/utils/date';
import dayjs from '~/utils/dayjs';
import { catchRequestError } from '~/utils/request';

export type BlastForecastItemAggregated = VxChartCalendarItemType & {
    num_corporate: number;
    num_org_units: number;
    num_groups: number;
    num_messages: number;
    num_blasts: number;
    intensity: VxChartCalendarIntensityLevel;
};

export const useBlastForecastSummary = createGlobalState(() => {
    const data = ref<BlastForecastItemAggregated[]>([]);
    const loading = ref(false);
    const error = ref<string>();

    const calculateIntensity = () => {
        const allValues = data.value.map((item) => item.value);
        const sortedValues = [...new Set(allValues.filter((value) => value > 0))].sort((a, b) => a - b);

        data.value.forEach((day) => {
            day.intensity = chartCalendarGetIntensityPercentile(sortedValues, day.num_messages);
        });
    };

    const fillAggregatedWithEmptyData = (
        fetchParams: GetBlastForecastRequest,
        aggregatedByDay: Map<string, BlastForecastItemAggregated>
    ) => {
        const [, endDate] = fetchParams.range;
        const today: Dayjs = dayjs().tz(getClientTz());
        const endDayjs: Dayjs = dayjs.utc(endDate).tz(getClientTz());

        let currentDate = today.startOf('day');
        while (currentDate.isSameOrBefore(endDayjs, 'day')) {
            const dateKey = currentDate.format('YYYY-MM-DD');
            aggregatedByDay.set(dateKey, {
                date: currentDate.toDate(),
                num_corporate: 0,
                num_org_units: 0,
                num_groups: 0,
                num_messages: 0,
                num_blasts: 0,
                value: 0,
                intensity: 'default',
            });
            currentDate = currentDate.add(1, 'day');
        }
    };

    const fetchData = async (clientContext: ClientContext, fetchParams: GetBlastForecastRequest) => {
        loading.value = true;
        error.value = undefined;

        try {
            const response = await blastForecastClient.getBlastForecastSummary(clientContext, fetchParams);

            const aggregatedByDay = new Map<string, BlastForecastItemAggregated>();

            fillAggregatedWithEmptyData(fetchParams, aggregatedByDay);

            response.data.items.forEach((item) => {
                const dateKey = dayjs.utc(item.live_at).tz(getClientTz()).format('YYYY-MM-DD');
                const existing = aggregatedByDay.get(dateKey);

                if (existing) {
                    existing.num_corporate += item.sender_type === 'corporate' ? item.num_blasts : 0;
                    existing.num_org_units += item.sender_type === 'org_unit' ? item.num_blasts : 0;
                    existing.num_groups += item.sender_type === 'group' ? item.num_blasts : 0;
                    existing.num_messages += item.num_messages;
                    existing.num_blasts += item.num_blasts;
                    existing.value = existing.num_messages;
                    // intensity is defined after aggregation so we can use minValue and maxValue
                    existing.intensity = 'default';
                } else {
                    aggregatedByDay.set(dateKey, {
                        date: dayjs.utc(item.live_at).tz(getClientTz()).startOf('day').toDate(),
                        num_corporate: item.sender_type === 'corporate' ? 1 : 0,
                        num_org_units: item.sender_type === 'org_unit' ? 1 : 0,
                        num_groups: item.sender_type === 'group' ? 1 : 0,
                        num_messages: item.num_messages,
                        num_blasts: item.num_blasts,
                        value: item.num_messages,
                        // intensity is defined after aggregation so we can use minValue and maxValue
                        intensity: 'default',
                    });
                }
            });

            data.value = Array.from(aggregatedByDay.values());
            calculateIntensity();
        } catch (e) {
            error.value = catchRequestError(e);
            data.value = [];
        } finally {
            loading.value = false;
        }
    };

    const reset = () => {
        data.value = [];
        loading.value = false;
        error.value = undefined;
    };

    const subtractCampaignFromDay = (date: Date, senderType: SenderType, audienceSize: number) => {
        const dateKey = dayjs(date).format('YYYY-MM-DD');
        const dayData = data.value.find((item) => dayjs(item.date).format('YYYY-MM-DD') === dateKey);

        if (dayData) {
            dayData.num_messages -= audienceSize;
            dayData.num_blasts -= 1;
            dayData.value = dayData.num_messages;

            switch (senderType) {
                case 'corporate':
                    dayData.num_corporate -= 1;
                    break;
                case 'org_unit':
                    dayData.num_org_units -= 1;
                    break;
                case 'group':
                    dayData.num_groups -= 1;
                    break;
            }

            calculateIntensity();
        }
    };

    return {
        data,
        loading,
        error,
        fetchData,
        reset,
        subtractCampaignFromDay,
    };
});
